/**
 * The resolved document the client is currently running on, and `flag()` — the one function a
 * flag is read through (R9 §4.1, D329).
 *
 * **Three sources, one order, no fourth (D328).** At module load the store holds the disk cache
 * if one reads, else the compiled-in defaults; a live fetch replaces it through
 * {@link acceptFetchedDocument} and nothing else does. There is no query-string override, no
 * `localStorage` dev key, no `import.meta.env.DEV` branch — `screens/shabbat/featureFlag.ts` had
 * one and it went with that file (R9 §10: *a developer flips `dev`'s parameter like an operator
 * does*).
 *
 * **A fetched document replaces the cache only when its `config_version` is not lower than the
 * cached one, and equal versions rewrite nothing** (R9 §3.2). A lower version is a container
 * serving a stale parameter read inside the server's 60 s window after a write; the client keeps
 * what it has and the next fetch settles it — but only inside {@link STALE_VERSION_WINDOW_MS} of
 * the higher one's fetch, past which a lower version is a parameter reset (D328 as amended by
 * v7.32).
 *
 * **`flag()` is memoized per document load** — the resolution happens once in
 * `resolveClientConfig` and a read is a property lookup — so a flag is read at its call site and
 * never re-read under an in-flight action (R9 §4.2). Consumers that must re-render on a new
 * document subscribe (`useFlag`); a bare read is for code that runs once.
 *
 * **The account identity is the store's second input, and it arrives after the first** (D321).
 * A cohorted flag's arm is `bucket < percent` over the signed-in account identity, and that
 * identity is not readable at module load — it lives in IndexedDB behind the credential store's
 * own open. So the store keeps the raw document beside the resolved one and re-resolves when
 * {@link setCohortIdentity} moves: boot resolves with no identity (every rule `off`, D325), and
 * the sign-in that follows publishes the real arms to the same subscribers a new document does.
 *
 * **The identity is PUSHED in from `App.tsx`, and the direction is the rule rather than the
 * convenience.** Nothing under `auth/` may import this directory: `screens/home/` imports
 * `auth/`, so an `auth/` → `config/` edge would put the whole open path one hop from the config
 * module and redden `infra/tests/client-config-client.test.ts`'s import-graph walk. `App.tsx` is
 * the shell seam that already holds both, and it is not on the open path — it imports Home, not
 * the reverse.
 *
 * Publishes like `pwa/updateAlerts.ts` and for the same reason: the consumers are screens already
 * mounted elsewhere, and a store nobody is told about moves nothing until an unrelated re-render.
 */

import type { ClientConfigDocument, FlagName } from '@selogate/core'
import { CLIENT_CONFIG_DEFAULTS } from '@selogate/core'
import { readCachedClientConfig, writeCachedClientConfig } from './cache'
import {
  DEFAULT_CLIENT_CONFIG,
  readClientConfigDocument,
  resolveClientConfig,
  type ResolvedClientConfig,
} from './document'

export type ClientConfigSource = 'live' | 'cache' | 'defaults'

/**
 * The account identity every cohorted rule is bucketed against, or `undefined` for a client that
 * has none. **Boot value is `undefined` and that is the honest one** — the credential store has
 * not opened yet, so this client is in no cohort until it says otherwise (D325).
 */
let identity: string | undefined

function fromDisk(): {
  readonly runningDocument: ClientConfigDocument
  readonly config: ResolvedClientConfig
  readonly source: ClientConfigSource
  readonly fetchedAt: number
} {
  const cached = readCachedClientConfig()
  return cached === undefined
    ? {
        runningDocument: CLIENT_CONFIG_DEFAULTS,
        config: DEFAULT_CLIENT_CONFIG,
        source: 'defaults',
        fetchedAt: 0,
      }
    : {
        runningDocument: cached.document,
        config: resolveClientConfig(cached.document, identity),
        source: 'cache',
        fetchedAt: cached.fetchedAt,
      }
}

/**
 * The body {@link current} was resolved from, kept so a new identity can re-resolve it without a
 * fetch. **Not named `document`**: a module-scope `document` shadows the DOM global for every
 * later line in this file, which is a trap rather than a name.
 */
let runningDocument: ClientConfigDocument
let current: ResolvedClientConfig
let source: ClientConfigSource
/** When the running document was fetched; `0` for the compiled-in defaults, which refuse nothing. */
let currentFetchedAt: number
;({ runningDocument, config: current, source, fetchedAt: currentFetchedAt } = fromDisk())

/**
 * **How long a LOWER `config_version` is refused after a higher one was fetched.** The stale rule
 * exists for one case — a server container inside its 60 s cache window answering with the
 * document before the operator's write — and that case can only produce a lower number within
 * about one TTL of the higher one. Left unbounded, the rule outlives its case: an SSM parameter
 * deleted and re-created, or a non-production stage torn down and re-applied, restarts `Version`
 * at 1, and every device that had cached a higher number would then refuse every document for
 * good, with no server-side symptom and no recovery short of clearing app storage. Five minutes is
 * five server TTLs, generous for the case and short enough that the reset reaches every device on
 * its next resume.
 */
export const STALE_VERSION_WINDOW_MS = 5 * 60 * 1000

const listeners = new Set<() => void>()

function publish(
  nextDocument: ClientConfigDocument,
  next: ResolvedClientConfig,
  nextSource: ClientConfigSource,
): void {
  runningDocument = nextDocument
  current = next
  source = nextSource
  for (const listener of listeners) listener()
}

/** `useSyncExternalStore`'s two halves. `getClientConfig` returns a stable reference. */
export function subscribeToClientConfig(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getClientConfig(): ResolvedClientConfig {
  return current
}

/** Which of D328's three sources the current document came from — for tests and the About-style read. */
export function getClientConfigSource(): ClientConfigSource {
  return source
}

/** The one reader (R9 §4.1). The type restricts the name to the registry; a literal elsewhere is the defect. */
export function flag(name: FlagName): boolean {
  return current.flags[name]
}

export type AcceptOutcome = 'accepted' | 'not-a-document' | 'stale' | 'unchanged'

/**
 * A live fetch's body. Returns what happened so the refresh rail can be tested without reading
 * the store back; nothing in the product reads the outcome.
 */
export function acceptFetchedDocument(raw: unknown, fetchedAt: number): AcceptOutcome {
  // `fetched`, not the store's `runningDocument`: the latter is the body the store is currently
  // resolved from, and `setCohortIdentity` re-resolves from it. Reassigning it here on a refused
  // body would re-resolve a document the store never accepted.
  const fetched = readClientConfigDocument(raw)
  if (fetched === undefined) return 'not-a-document'
  if (fetched.config_version < current.configVersion) {
    // Refused only while the higher number is young enough to be a stale container's answer
    // (`STALE_VERSION_WINDOW_MS`); older than that, a lower number is a parameter reset and the
    // fetched document is the live one.
    if (fetchedAt - currentFetchedAt < STALE_VERSION_WINDOW_MS) return 'stale'
  } else if (fetched.config_version === current.configVersion) {
    return 'unchanged'
  }
  writeCachedClientConfig(fetched, fetchedAt)
  currentFetchedAt = fetchedAt
  publish(fetched, resolveClientConfig(fetched, identity), 'live')
  return 'accepted'
}

/**
 * The signed-in account identity, or `undefined` when there is none (sign-out, or a boot that has
 * not resolved a session yet). Re-resolves the running document and notifies — **the same
 * publish a new document takes**, so a surface behind `useFlag` re-renders on a sign-in exactly
 * as it does on a flip.
 *
 * **Equal values are a no-op**, so the shell may call it on every session change without
 * republishing: a bucket that is recomputed is a bucket that can be recomputed differently, and
 * the done-when here is a stability claim.
 *
 * **This is the account identity — `SessionState.userId` — and not the Cognito `sub` D321's row
 * names.** The client never receives a `sub`: `packages/functions/auth/identity.ts` mints
 * `userId` as its own `randomUUID()` and keeps `cognitoSub` as an *attribute* of that row,
 * precisely so D49's pool loss is repaired by `rewriteCognitoSub` rather than by re-keying every
 * membership. So the `sub` is both unavailable here **and** the less stable of the two — a
 * restore would re-roll every resident's cohort under it, which is the failure D321's stability
 * half was written against. D321 is corrected in place at its owner; see the PRD row.
 */
export function setCohortIdentity(next: string | undefined): void {
  if (next === identity) return
  identity = next
  publish(runningDocument, resolveClientConfig(runningDocument, identity), source)
}

/** Test seam: re-read the disk (which the test has usually just cleared or seeded) and notify. */
export function __resetClientConfigForTests(): void {
  const next = fromDisk()
  currentFetchedAt = next.fetchedAt
  publish(next.runningDocument, next.config, next.source)
}
